import * as Sentry from '@sentry/react'
import { AxiosPromise } from 'axios'
import API from 'shared/api/API'
import Cookies from 'shared/helpers/Cookies'

import { AuthCredentials, TeacherData, TeacherProfile, TeacherProfileUpdate, TeacherSettings } from './types'

interface AuthorizationResponse {
  user: TeacherData
  token: string
}

class AuthApi {
  static getUserToken = () => Cookies.getItem('teacher_token')
  static setUserToken = (token: string) => Cookies.setItem('teacher_token', token, 100)
  static removeUserToken = () => Cookies.removeItem('teacher_token')
  static setTeacherSettings = (settings: TeacherSettings) =>
    localStorage.setItem('teacher_settings', JSON.stringify(settings))

  static getTeacherSettings = (): TeacherSettings | null => {
    const settings = localStorage.getItem('teacher_settings')
    if (!settings) return null
    return JSON.parse(settings) as TeacherSettings
  }

  static login = ({
    captchaToken,
    ...data
  }: AuthCredentials & { captchaToken: string }): AxiosPromise<AuthorizationResponse> =>
    API.post('/auth/teacher', data, { headers: { 'x-astrid-recaptcha-token': captchaToken } })
  static create = (data: AuthCredentials): AxiosPromise<AuthorizationResponse> => API.post('/auth/teacher/signup', data)
  static createProfile = (data: TeacherProfile): AxiosPromise<TeacherData> => API.post('/teacher/profile', data)
  static load = (): AxiosPromise<TeacherData> => API.get('/teacher')
  static verify = (token: string) => API.get(`/auth/verify/${token}`)
  static verifyEmailChange = (token: string) => API.get(`/auth/verify-change/${token}`)
  static setOnboardingCompleted = (): AxiosPromise<TeacherData> => API.patch('/teacher', { onboardingCompleted: true })
  static setTutorialCompleted = (): AxiosPromise<TeacherData> => API.patch('/teacher', { tutorialCompleted: true })
  static logout = () => {
    AuthApi.removeUserToken()
    Sentry.setUser(null)
    // redirect to root if not already there
    if (window.location.pathname !== '/') {
      window.location.href = '/'
    }
  }
  static sendRecoverPasswordEmail = (email: string, captchaToken: string) =>
    API.get(`/auth/teacher/reset-pass/${email}`, { headers: { 'x-astrid-recaptcha-token': captchaToken } })
  static setNewPassword = (password: string, token: string) =>
    API.post('/auth/reset-pass', { password }, { headers: { authorization: token } })
  static resendVerificationEmail = () => API.post('/auth/resend')
  static deleteAccount = () => API.delete('/teacher')
  static changePassword = (password: string) => API.patch('/teacher', { password })
  static requestEmailChange = (email: string) => API.patch('/teacher', { email })
  static updateProfile = (data: TeacherProfileUpdate): AxiosPromise<TeacherData> => API.patch('/teacher/profile', data)
  static setProfileAvatar = (data: FormData): AxiosPromise<TeacherData> => API.put('/teacher/profile/avatar', data)
}

export default AuthApi
